import type { CollectionData, RecordData } from '@tomo/core';
import { Tag } from '@tomo/ui';
import { Link2, Vote } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { useBacklinks, useCollections, usePolls, useRecordMap, useSession } from '../../data/hooks.tsx';
import { RecordChip } from '../fields/display.tsx';

/** Everything that points at this record: mentions, relations and polls. */
export function RelatedPanel({ record }: { record: RecordData; collection: CollectionData }) {
  const session = useSession();
  const links = useBacklinks(record.id);
  const recordMap = useRecordMap();
  const collections = useCollections();
  const polls = usePolls();
  const byId = useMemo(() => new Map(collections.map((c) => [c.id, c])), [collections]);

  const linked = links.map((id) => recordMap.get(id)).filter(Boolean) as RecordData[];
  const relatedPolls = polls.filter((p) => p.linkedRecordId === record.id || p.options.some((o) => o.recordId === record.id));

  // One group per collection: no collection is special, so none of them is singled out.
  const groups = new Map<string, RecordData[]>();
  for (const r of linked) {
    const list = groups.get(r.collectionId) ?? [];
    list.push(r);
    groups.set(r.collectionId, list);
  }

  if (!linked.length && !relatedPolls.length) {
    return (
      <section className="related">
        <h3 className="related__title">
          <Link2 size={14} /> Linked from
        </h3>
        <p className="related__empty">Nothing links here yet. Mention this record with @ in any doc, or link it from a relation field.</p>
      </section>
    );
  }

  return (
    <section className="related">
      {groups.size > 0 && (
        <div className="related__block">
          <h3 className="related__title">
            <Link2 size={14} /> Linked from <span className="related__count">{linked.length}</span>
          </h3>
          {[...groups].map(([cid, recs]) => (
            <div key={cid} className="related__group">
              <div className="related__group-name">{byId.get(cid)?.name}</div>
              <div className="related__chips">
                {recs.map((r) => (
                  <RecordChip key={r.id} record={r} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {relatedPolls.length > 0 && (
        <div className="related__block">
          <h3 className="related__title">
            <Vote size={14} /> Votes <span className="related__count">{relatedPolls.length}</span>
          </h3>
          <div className="related__list">
            {relatedPolls.map((p) => (
              <Link key={p.id} to={`/p/${session.id}/votes#${p.id}`} className="related__task">
                <span className="g-truncate">{p.question}</span>
                <Tag size="sm" color={p.status === 'open' ? 'green' : 'gray'}>
                  {p.status === 'open' ? 'Open' : 'Decided'}
                </Tag>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
